import {
  ApiError,
  errorResponseSchema,
  NETWORK_ERROR_CODE,
  NETWORK_ERROR_MESSAGE,
  UNEXPECTED_RESPONSE_CODE,
  UNEXPECTED_RESPONSE_MESSAGE,
} from '../api/errors';
import { CSRF_HEADER_NAME, getCsrfToken } from '../api/http';
import { noteResponseSchema, type NoteResponse } from '../notes/schema';

export interface UploadNoteParams {
  readonly file: File;
  readonly idempotencyKey: string;
  readonly capturedAt: string | undefined;
  /** Real upload-body progress, `0`..`1`. Called from `XMLHttpRequest.upload.onprogress`. */
  readonly onProgress?: (fraction: number) => void;
}

function parseJsonBody(text: string): unknown {
  if (text === '') {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/** Poll only as a recovery path for a browser that loses every terminal XHR callback. */
const XHR_COMPLETION_POLL_MS = 100;

/**
 * `POST /notes` as `multipart/form-data`, over `XMLHttpRequest` rather than the `fetch`-based
 * `api/http.ts` boundary: `fetch` has no portable way to report upload-body progress, and the
 * capture screen's progress bar must be real (`docs/implementation/06-mobile-web.md` 6.2).
 * Everything else matches that boundary's conventions on purpose - the session travels as the
 * `HttpOnly` cookie (`withCredentials`), the readable CSRF cookie is echoed because this is a
 * non-safe method (`getCsrfToken`, shared with `api/http.ts`), and the response is validated
 * with the same `zod`/`ApiError` pattern (`errorResponseSchema` on failure, `noteResponseSchema`
 * on success).
 */
export function uploadNote({
  file,
  idempotencyKey,
  capturedAt,
  onProgress,
}: UploadNoteParams): Promise<NoteResponse> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('image', file);
    form.append('idempotency_key', idempotencyKey);
    if (capturedAt !== undefined) {
      form.append('captured_at', capturedAt);
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/notes');
    xhr.withCredentials = true;

    const csrfToken = getCsrfToken();
    if (csrfToken !== null) {
      xhr.setRequestHeader(CSRF_HEADER_NAME, csrfToken);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded / event.total);
      }
    };

    // Some mobile WebViews have been observed to receive a complete response for a large
    // multipart upload but lose every terminal callback. The poll only observes this one XHR's
    // state; it never sends or retries anything. Every completion path clears it.
    let settled = false;
    let completionPoll: ReturnType<typeof setInterval> | undefined;
    function stopCompletionPoll(): void {
      if (completionPoll !== undefined) {
        clearInterval(completionPoll);
        completionPoll = undefined;
      }
    }

    function settleFromResponse(): void {
      if (settled) {
        return;
      }
      settled = true;
      stopCompletionPoll();
      const body = parseJsonBody(xhr.responseText);

      // Chromium can end an interrupted request with `loadend`/DONE but no `error` event.
      // Status zero still means that no HTTP response was available and remains a safe
      // same-idempotency-key retry, just like the explicit network-error callback.
      if (xhr.status === 0) {
        reject(new ApiError(0, NETWORK_ERROR_CODE, NETWORK_ERROR_MESSAGE));
        return;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        const parsed = noteResponseSchema.safeParse(body);
        if (!parsed.success) {
          reject(new ApiError(xhr.status, UNEXPECTED_RESPONSE_CODE, UNEXPECTED_RESPONSE_MESSAGE));
          return;
        }
        onProgress?.(1);
        resolve(parsed.data);
        return;
      }

      const parsedError = errorResponseSchema.safeParse(body);
      if (parsedError.success) {
        reject(new ApiError(xhr.status, parsedError.data.code, parsedError.data.message));
        return;
      }
      reject(new ApiError(xhr.status, UNEXPECTED_RESPONSE_CODE, UNEXPECTED_RESPONSE_MESSAGE));
    }

    xhr.onerror = () => {
      if (settled) {
        return;
      }
      settled = true;
      stopCompletionPoll();
      reject(new ApiError(0, NETWORK_ERROR_CODE, NETWORK_ERROR_MESSAGE));
    };

    xhr.onload = settleFromResponse;
    xhr.onloadend = settleFromResponse;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        settleFromResponse();
      }
    };
    completionPoll = setInterval(() => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        settleFromResponse();
      }
    }, XHR_COMPLETION_POLL_MS);

    xhr.send(form);
  });
}
